import { useProfile } from "../context/ProfileContext";

export default function TeamAffiliations() {
  const PROFILE = useProfile();
  return (
    <section className="relative bg-[#0A0A0A] py-24 sm:py-32 border-t border-white/10" data-testid="team-section">
      <div className="max-w-screen-2xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="mb-12">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-4">
            03 — Programs
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase font-bold tracking-tight text-white leading-none">
            Club + School
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
          {/* Team photo - large */}
          <div className="lg:col-span-8 relative overflow-hidden border border-white/10 group">
            <img
              src={PROFILE.images.team}
              alt="A5 Volleyball 17-2 Angel team photo"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              style={{ minHeight: 420 }}
              data-testid="team-photo"
            />
            <div className="absolute top-6 left-6 inline-flex items-center gap-2 bg-[#E60000] px-3 py-1.5">
              <span className="text-[10px] tracking-[0.3em] uppercase text-white font-bold">
                Team Photo · A5 17-2 Angel
              </span>
            </div>
          </div>

          {/* Affiliations */}
          <div className="lg:col-span-4 flex flex-col gap-4 sm:gap-6">
            <div className="border border-white/10 p-8 hover:bg-[#111111] transition-colors flex-1" data-testid="club-card">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold">
                  Club
                </div>
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/95 flex items-center justify-center p-1.5 shrink-0">
                  <img
                    src={PROFILE.clubLogo}
                    alt="A5 Volleyball Club logo"
                    className="w-full h-full object-contain"
                    data-testid="club-logo"
                  />
                </div>
              </div>
              <div className="font-display text-3xl uppercase font-bold tracking-tight text-white leading-tight">
                A5 Volleyball Club
              </div>
              <div className="font-display text-xl uppercase text-[#8A8A8A] mt-1">
                17-2 Angel · Roswell, GA
              </div>
              <div className="mt-6 flex items-baseline gap-3">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#8A8A8A]">Jersey</span>
                <span className="font-display text-5xl font-black text-[#E60000] tracking-tighter leading-none">
                  #{PROFILE.clubJersey}
                </span>
              </div>
              <div className="mt-6 pt-6 border-t border-white/10 text-xs text-[#8A8A8A] leading-relaxed">
                One of the top club programs in the country.
              </div>
            </div>

            <div className="border border-white/10 p-8 hover:bg-[#111111] transition-colors flex-1" data-testid="school-card">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold">
                  High School
                </div>
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/95 flex items-center justify-center p-1.5 shrink-0">
                  <img
                    src={PROFILE.schoolLogo}
                    alt="Westminster Wildcats logo"
                    className="w-full h-full object-contain"
                    data-testid="school-logo"
                  />
                </div>
              </div>
              <div className="font-display text-3xl uppercase font-bold tracking-tight text-white leading-tight">
                Westminster Wildcats
              </div>
              <div className="font-display text-xl uppercase text-[#8A8A8A] mt-1">
                Atlanta, GA · The Westminster Schools
              </div>
              <div className="mt-6 flex items-baseline gap-3">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#8A8A8A]">Jersey</span>
                <span className="font-display text-5xl font-black text-[#E60000] tracking-tighter leading-none">
                  #{PROFILE.schoolJersey}
                </span>
              </div>
              <div className="mt-6 pt-6 border-t border-white/10 text-xs text-[#8A8A8A] leading-relaxed">
                2025 GHSA Class AAAA Final Four
              </div>
            </div>
          </div>
        </div>

        {/* Governing-body IDs */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 border-t border-l border-white/10" data-testid="governing-ids">
          <div className="border-r border-b border-white/10 p-6 flex items-center justify-between gap-4">
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold">USAV ID</span>
            <span className="font-display text-2xl sm:text-3xl uppercase font-bold tracking-tight text-white" data-testid="usav-id">
              {PROFILE.usavId}
            </span>
          </div>
          <div className="border-r border-b border-white/10 p-6 flex items-center justify-between gap-4">
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold">AAU ID</span>
            <span className="font-display text-2xl sm:text-3xl uppercase font-bold tracking-tight text-white" data-testid="aau-id">
              {PROFILE.aauId}
            </span>
          </div>
        </div>

        {/* Tournament Strip */}
        <div className="mt-12 border-t border-white/10 pt-10" data-testid="tournaments">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-6">
            2025–26 Season · Selected Tournaments
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10">
            {PROFILE.tournaments.map((t, i) => (
              <div
                key={t}
                className="border-r border-b border-white/10 p-6 flex flex-col gap-3"
              >
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#8A8A8A]">
                  Event 0{i + 1}
                </span>
                <span className="font-display text-xl sm:text-2xl uppercase font-bold tracking-tight text-white leading-tight">
                  {t}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
